import { DIMENSIONS, useDimensionEntry } from "../lib/dimensionDefs.js";
import { getDimAdapterByPlural } from "../lib/dimAdapters.js";
import { deriveAgentIdentity } from "../lib/agentIdentity.js";
import { LOGO_DEFS, PLACEHOLDER_LOGO } from "../lib/dimensionRenderers.js";
import OverflowTooltip from "./OverflowTooltip.jsx";

// Render-facet resolution: built-in dim definitions (_ALL_DIMENSIONS)
// take priority for backward compatibility; adapters fill in for any
// dim that doesn't have a built-in facet. Future synthetic / external
// adapters that want an icon or a secondary line just declare it on
// their adapter — no edits to the components below required.
function resolveFacet(dim, facet) {
  const builtin = DIMENSIONS[dim]?.[facet];
  if (builtin) return builtin;
  return getDimAdapterByPlural(dim)?.[facet] ?? null;
}

// Drop-in primitives that read from the dim registry's render facets
// (renderIcon / renderSecondary / renderTone) plus the lazy label
// cache. Co-exist with existing scopeStepLabel-based rendering — call
// sites adopt these incrementally.
//
// DimensionIcon  → SVG mark for dims with renderIcon (e.g. agents);
//                  returns null for dims without one (no fallback dot,
//                  so the call site decides whether to leave a gap).
// DimensionLabel → primary label text, reactive via cache subscription.
//                  Equivalent to useLabel for a (dim, id) pair, but
//                  scoped to a single entity rather than a step.

// Icons render in `currentColor` so they pick up the surrounding text
// color; brand tones from renderTone are exposed via useDimensionEntry
// for callers that explicitly want to opt in (e.g. a tinted badge),
// but the default mark is monochrome to match the rest of the chrome.
export function DimensionIcon({ dim, id, className = "h-3 w-3", style, title }) {
  const { meta } = useDimensionEntry(dim, id);
  const renderIcon = resolveFacet(dim, "renderIcon");
  if (!renderIcon) return null;
  const spec = renderIcon(id, meta);
  if (!spec) return null;
  return (
    <svg
      viewBox={spec.viewBox}
      className={className}
      style={style}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role={title ? "img" : undefined}
    >
      {title ? <title>{title}</title> : null}
      {spec.paths.map((d, i) => (
        <path key={i} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}

// Wrapped in OverflowTooltip so a hover reveals the full label when
// the text gets truncated by the surrounding container (sidebar rows,
// scope-column entries, tab strips). Tooltip is a no-op when the
// label fits; no UI noise on short values.
export function DimensionLabel({ dim, id, className, fallback }) {
  const { label } = useDimensionEntry(dim, id);
  const text = label || fallback || String(id ?? "");
  return <OverflowTooltip text={text} className={className} />;
}

// Secondary line for dims with a renderSecondary facet (e.g. an
// endpoint's registered user). Returns null when the dim has no
// secondary or the meta hasn't arrived yet, so call sites can render
// it unconditionally without leaving an empty slot. Also wrapped in
// OverflowTooltip — registered-user emails routinely outrun their
// cell width.
export function DimensionSecondary({ dim, id, className }) {
  const { meta } = useDimensionEntry(dim, id);
  const renderSecondary = resolveFacet(dim, "renderSecondary");
  if (!renderSecondary) return null;
  const text = renderSecondary(id, meta);
  if (!text) return null;
  return <OverflowTooltip text={text} className={className} />;
}

// Non-hook agent icon for tabular / list contexts where the agent
// name is already known and async label resolution is unnecessary.
// Skips useDimensionEntry's useState/useEffect + cache subscription,
// so a table that renders hundreds of these doesn't pay for hundreds
// of subscribers (each notified on every label-cache update). Use
// DimensionIcon for scope-path / dim-registry-driven UI; use this for
// "render an icon for this raw provider/agent_type string" cases.
export function AgentIcon({ name, className = "h-3 w-3", style, title }) {
  if (!name) return null;
  const identity = deriveAgentIdentity(name);
  const spec = LOGO_DEFS[identity.key] || PLACEHOLDER_LOGO;
  return (
    <svg
      viewBox={spec.viewBox}
      className={className}
      style={style}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role={title ? "img" : undefined}
    >
      {title ? <title>{title}</title> : null}
      {spec.paths.map((d, i) => (
        <path key={i} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}
